
import { useState, useEffect } from 'react';

export interface Payment {
  id: string;
  patientName: string;
  patientId: string;
  service: string;
  amount: number;
  date: string;
  status: 'paid' | 'pending' | 'transferred';
  invoiceUrl?: string;
  caseId?: string;
  sessionId?: string;
}

export interface PaymentSummary {
  totalEarnings: number;
  withdrawableBalance: number;
  pendingTransfers: number;
}

interface UseDoctorPaymentsParams {
  status?: string;
  dateFrom?: Date;
  dateTo?: Date;
}

export const useDoctorPayments = (params?: UseDoctorPaymentsParams) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [summary, setSummary] = useState<PaymentSummary>({
    totalEarnings: 0,
    withdrawableBalance: 0,
    pendingTransfers: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPayments = async () => {
      setIsLoading(true);
      
      try {
        // In a real app, this would be an API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Mock data
        const mockPayments: Payment[] = [
          {
            id: '1',
            patientName: 'John Smith',
            patientId: 'pat-001',
            service: 'Dental Cleaning',
            amount: 120.00,
            date: '2025-03-15',
            status: 'paid',
            invoiceUrl: 'https://example.com/invoice/1',
            caseId: 'case-001',
            sessionId: 'session-001'
          },
          {
            id: '2',
            patientName: 'Emma Johnson',
            patientId: 'pat-002',
            service: 'Root Canal',
            amount: 450.00,
            date: '2025-03-10',
            status: 'transferred',
            invoiceUrl: 'https://example.com/invoice/2',
            caseId: 'case-002',
            sessionId: 'session-002'
          },
          {
            id: '3',
            patientName: 'Michael Chen',
            patientId: 'pat-003',
            service: 'Teeth Whitening',
            amount: 200.00,
            date: '2025-04-01',
            status: 'pending',
            caseId: 'case-003',
            sessionId: 'session-003'
          },
          {
            id: '4',
            patientName: 'Sarah Rodriguez',
            patientId: 'pat-004',
            service: 'Dental Implant',
            amount: 1200.00,
            date: '2025-03-28',
            status: 'paid',
            invoiceUrl: 'https://example.com/invoice/4',
            caseId: 'case-004',
            sessionId: 'session-004'
          },
          {
            id: '5',
            patientName: 'David Kim',
            patientId: 'pat-005',
            service: 'Orthodontic Consultation',
            amount: 85.00,
            date: '2025-03-22',
            status: 'transferred',
            invoiceUrl: 'https://example.com/invoice/5',
            caseId: 'case-005',
            sessionId: 'session-005'
          }
        ];
        
        // Filter by status if provided
        let filteredPayments = [...mockPayments];
        
        if (params?.status) {
          filteredPayments = filteredPayments.filter(p => p.status === params.status);
        }
        
        // Filter by date range if provided
        if (params?.dateFrom && params?.dateTo) {
          filteredPayments = filteredPayments.filter(p => {
            const paymentDate = new Date(p.date);
            return paymentDate >= params.dateFrom! && paymentDate <= params.dateTo!;
          });
        }
        
        // Sort by date (newest first)
        filteredPayments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        
        setPayments(filteredPayments);
        
        // Calculate summary data
        const totalEarnings = mockPayments.reduce((sum, p) => sum + p.amount, 0);
        const pendingTransfers = mockPayments
          .filter(p => p.status === 'pending')
          .reduce((sum, p) => sum + p.amount, 0);
        
        // Withdrawable is total - pending (simplified logic)
        const withdrawableBalance = mockPayments
          .filter(p => p.status === 'paid')
          .reduce((sum, p) => sum + p.amount, 0);
        
        setSummary({
          totalEarnings,
          withdrawableBalance,
          pendingTransfers
        });
        
      } catch (error) {
        console.error('Error fetching payments:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPayments();
  }, [params?.status, params?.dateFrom, params?.dateTo]);
  
  return {
    payments,
    summary,
    isLoading
  };
};
